import React, { useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import moment from 'moment';
import CSSTransition from 'react-transition-group/CSSTransition';

import fadeAnimation from 'components/animations/fadeAnimation.module.scss';
import OutsideClickHandler from 'components/OutsideClickHandler';
import SubscriptionCancellationDate from 'components/SubscriptionCancellationDate/SubscriptionCancellationDate';
import Toast from 'components/Toast/Toast';
import BillingCapacityMove from 'Customers/BillingCapacity/BillingCapacityMove';
import StatusChip from 'Customers/components/StatusChip/StatusChip';
import {
  useMutationActivateSubscription,
  useMutationDeactivateSubscription,
} from 'Customers/Subscriptions/hooks/useSubscriptions';
import { getSubscriptionMenu } from 'Customers/utility';
import useToast from 'hooks/useToast';
import { useVisibility } from 'Shared/useVisibility';
import formatSubscriptionBusinessDate from 'utility/formatSubscriptionBusinessDate';

import { PACKAGE_NAMES } from '../../../constants';
import EditMenu from '../EditMenu/EditMenu';
import EditSubscriptionModal from '../EditSubscriptionModal/EditSubscriptionModal';

const TenantSubscriptionsTableRow = ({
  plan,
  isPrimary,
  status,
  expires,
  cancellationDate,
  startDate,
  mrr,
  id,
  price,
  implementationUser,
  numberOfUnits,
  pricingModel,
  currency,
  requiresUser,
  billingCapacity,
  pricingPeriod,
  pricingPeriodUnit,
  onOpenMove,
}) => {
  const queryClient = useQueryClient();
  const [editModal, setEditModal] = useState(false);
  const [movePreview, setMovePreview] = useState(null);
  const [moveRefresh, setMoveRefresh] = useState(0);
  const [purchaseReceipt, setPurchaseReceipt] = useState(null);
  const { toast, showLoading, showSuccess, showError, hideToast } = useToast();
  const { isOpen: isMenuOpen, open: handleMenuOpen, close: handleCloseMenu } = useVisibility();
  const { mutate: activateMutate } = useMutationActivateSubscription();
  const { mutate: deactivateMutate } = useMutationDeactivateSubscription();
  const data = { price, implementationUser, numberOfUnits };
  const selected = {
    isPrimary,
    price,
    implementationUser,
    numberOfUnits,
    id,
    plan,
    pricingModel,
    requiresUser,
    billingCapacity,
    pricingPeriod,
    pricingPeriodUnit,
    currency,
  };

  const handleSuccess = async () => {
    await queryClient.invalidateQueries(
      {
        queryKey: ['TenantSubscriptions'],
      },
      { exact: true }
    );
  };

  const handleAction = (action) => {
    switch (action) {
      case 'Delete':
        handleCloseMenu();
        showLoading(isPrimary ? 'Cancelling subscription...' : 'Opting out...');
        deactivateMutate(
          { id, data },
          {
            onSuccess: () => {
              handleSuccess();
              showSuccess(isPrimary ? 'Subscription cancelled successfully' : 'Successfully opted out');
            },
            onError: (error) => {
              showError(error?.response?.data?.message || 'Failed to update subscription');
            },
          }
        );
        break;
      case 'Activate':
        handleCloseMenu();
        showLoading('Activating subscription...');
        activateMutate(
          { id, data },
          {
            onSuccess: () => {
              handleSuccess();
              showSuccess('Subscription activated successfully');
            },
            onError: (error) => {
              showError(error?.response?.data?.message || 'Failed to activate subscription');
            },
          }
        );
        break;
      case 'Edit':
        setMovePreview(null);
        handleCloseMenu();
        setEditModal(true);
        break;
      default:
        break;
    }
  };
  return (
    <>
      <tr>
        <td className='d-flex align-items-center gap-2 py-3 px-3'>
          <p>{PACKAGE_NAMES[plan] || plan}</p>
          {isPrimary && <i className='icon-verified1-fill text-green me-2' />}
          {isPrimary && (
            <BillingCapacityMove
              onOpen={onOpenMove ? () => onOpenMove(selected) : undefined}
              subscriptionId={id}
              available={
                !billingCapacity ||
                billingCapacity.originalMovePending ||
                ['legacy', 'setup_pending'].includes(billingCapacity.mode)
              }
              billingCapacity={billingCapacity}
              plan={PACKAGE_NAMES[plan] || plan}
              purchaseReceipt={purchaseReceipt}
              refreshKey={moveRefresh}
              onSuccess={handleSuccess}
              onEdit={(preview) => {
                setMovePreview(preview);
                setEditModal(true);
              }}
            />
          )}
        </td>
        <td>
          <StatusChip status={status} />
        </td>
        <td>
          {currency} {mrr}
        </td>
        <td>{startDate ? moment(startDate).format('MMM DD, YYYY') : '--'}</td>
        <td>
          {formatSubscriptionBusinessDate(expires) || '--'}
          <SubscriptionCancellationDate status={status} cancellationDate={cancellationDate} />
        </td>
        <td>
          <div className='position-relative'>
            <div className='d-flex align-items-center justify-content-start'>
              <i
                className='icon-more-horizontal text-gray'
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  handleMenuOpen();
                }}
              />
            </div>
            <CSSTransition
              mountOnEnter
              unmountOnExit
              in={isMenuOpen}
              timeout={200}
              classNames={{
                enter: fadeAnimation.enter,
                enterActive: fadeAnimation.enterActive,
                exit: fadeAnimation.exit,
                exitActive: fadeAnimation.exitActive,
              }}
            >
              <OutsideClickHandler
                clickHandler={() => {
                  handleCloseMenu();
                }}
              >
                <EditMenu items={getSubscriptionMenu(status, isPrimary)} onAction={handleAction} />
              </OutsideClickHandler>
            </CSSTransition>
          </div>
        </td>
      </tr>
      <EditSubscriptionModal
        isOpen={editModal}
        selected={selected}
        billingPreview={movePreview}
        onPurchaseComplete={setPurchaseReceipt}
        onClose={() => {
          setEditModal(false);
          setMovePreview(null);
          setMoveRefresh((current) => current + 1);
        }}
      />
      <Toast {...toast} onClose={hideToast} />
    </>
  );
};

export default TenantSubscriptionsTableRow;
